import React, { useState, useEffect, useMemo } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { 
  collection, 
  onSnapshot, 
  doc, 
  deleteDoc,
  addDoc,
  updateDoc
} from "firebase/firestore";
import {
  Search, Trash2, Printer, Filter, 
  Eye, Users, Activity, Edit3, Plus, X, Stethoscope, User as UserIcon, Calendar, PieChart as PieChartIcon, Download, ChevronDown
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Sidebar from "../components/Sidebar";

const INITIAL_FORM_STATE = {
  storeId: "", // Super Admin needs to pick a store
  patientName: "", patientMobile: "", patientAge: "", patientGender: "Male",
  doctorName: "", prescriptionNotes: "",
  sphRight: "", cylRight: "", axisRight: "", pdRight: "", vaRight: "", addRight: "", nvSphRight: "", nvCylRight: "", nvAxisRight: "",
  sphLeft: "", cylLeft: "", axisLeft: "", pdLeft: "", vaLeft: "", addLeft: "", nvSphLeft: "", nvCylLeft: "", nvAxisLeft: "",
  clSphRight: "", clCylRight: "", clAxisRight: "", clSphLeft: "", clCylLeft: "", clAxisLeft: "",
  lensTypes: []
};

const ALL_LENS_TYPES = ['Single Vision', 'Bifocal', 'Progressive', 'Contact Lens', 'Constant Use', 'Reading Wear', 'Distance Wear', 'Trifocal'];
const COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2'];

export default function SuperAdminPrescription() {
  const [stores, setStores] = useState({});
  const [prescriptions, setPrescriptions] = useState([]);
  
  // Filter States
  const [filters, setFilters] = useState({ 
    query: "", 
    startDate: "", 
    endDate: "", 
    doctorName: "", 
    lensType: "",
    storeId: "",
    recordsPerPage: 50 
  });

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingStoreId, setEditingStoreId] = useState(null); // Needed to track original store during edit
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [activeTab, setActiveTab] = useState("eyewear");

  // --- 1. FETCH STORES AND THEIR PRESCRIPTIONS SAFELY ---
  // Replaced collectionGroup with nested listeners to bypass strict Firestore rules
  useEffect(() => {
    let rxUnsubscribers = [];

    const unsubStores = onSnapshot(collection(db, "stores"), (storesSnap) => {
      const storesMap = {};
      storesSnap.docs.forEach(doc => { storesMap[doc.id] = { id: doc.id, ...doc.data() }; });
      setStores(storesMap);

      // Clear existing listeners to prevent duplicates
      rxUnsubscribers.forEach(unsub => unsub());
      rxUnsubscribers = [];

      // Set up a listener for each individual store's prescriptions
      storesSnap.docs.forEach(storeDoc => {
        const unsubRx = onSnapshot(collection(db, "stores", storeDoc.id, "prescriptions"), (rxSnap) => {
          const records = rxSnap.docs.map(d => ({
            id: d.id,
            parentStoreId: storeDoc.id,
            ...d.data()
          }));

          setPrescriptions(prev => {
            // Remove old records from this specific store to avoid duplication, then merge new ones
            const otherStoreRecords = prev.filter(p => p.parentStoreId !== storeDoc.id);
            const combined = [...otherStoreRecords, ...records];
            
            // Safe robust sorting for mixed timestamp formats
            return combined.sort((a, b) => {
              const timeA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : new Date(a.timestamp || 0).getTime();
              const timeB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : new Date(b.timestamp || 0).getTime();
              return timeB - timeA;
            });
          });
        });
        
        rxUnsubscribers.push(unsubRx);
      });
    });

    return () => {
      unsubStores();
      rxUnsubscribers.forEach(unsub => unsub());
    };
  }, []);

  // --- DATE HELPER (Robust for all timestamp types) ---
  const parseDate = (ts) => {
    if (!ts) return null;
    return ts.toDate ? ts.toDate() : new Date(ts);
  };

  const formatDate = (ts) => {
    const dateObj = parseDate(ts);
    if (!dateObj || isNaN(dateObj)) return "-";
    return dateObj.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '');
  };

  // --- DERIVED DATA & FILTERS ---
  const uniqueDoctors = useMemo(() => Array.from(new Set(prescriptions.map(p => p.doctor?.name || p.doctorName).filter(Boolean))), [prescriptions]);

  const filteredData = useMemo(() => {
    let result = prescriptions;

    if (filters.query) {
      const q = filters.query.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(q) || p.phone?.includes(q) || 
        p.doctorName?.toLowerCase().includes(q) || p.patient?.name?.toLowerCase().includes(q) ||
        p.patient?.mobile?.includes(q)
      );
    }
    if (filters.startDate) {
      result = result.filter(p => {
        const pDate = parseDate(p.timestamp);
        return pDate && pDate >= new Date(filters.startDate);
      });
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(p => {
        const pDate = parseDate(p.timestamp);
        return pDate && pDate <= end;
      });
    }
    if (filters.doctorName) result = result.filter(p => (p.doctor?.name || p.doctorName) === filters.doctorName);
    if (filters.lensType) result = result.filter(p => p.lensTypes?.includes(filters.lensType));
    if (filters.storeId) result = result.filter(p => p.parentStoreId === filters.storeId);

    return result;
  }, [prescriptions, filters]);

  // Dashboard Aggregations
  const dashboardStats = useMemo(() => {
    const total = filteredData.length;
    const storeCounts = {};
    
    filteredData.forEach(p => {
      const storeName = stores[p.parentStoreId]?.storeId || "Unknown Store";
      storeCounts[storeName] = (storeCounts[storeName] || 0) + 1;
    });

    const pieData = Object.keys(storeCounts).map(key => ({ name: key, value: storeCounts[key] }));

    return { total, pieData, storeSummary: storeCounts };
  }, [filteredData, stores]);

  // --- ACTIONS ---
  const handleDelete = async (storeId, id) => {
    if (!window.confirm(`Delete this prescription record permanently?`)) return;
    try { await deleteDoc(doc(db, "stores", storeId, "prescriptions", id)); } 
    catch (error) { alert("Error deleting record."); }
  };

  const handleOpenAddModal = () => {
    setFormData(INITIAL_FORM_STATE);
    setEditingId(null);
    setEditingStoreId(null);
    setActiveTab("eyewear");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setFormData({
      ...INITIAL_FORM_STATE,
      storeId: record.parentStoreId,
      patientName: record.patient?.name || record.name || "",
      patientMobile: record.patient?.mobile || record.phone || "",
      patientAge: record.patient?.age || "",
      patientGender: record.patient?.gender || "Male",
      doctorName: record.doctor?.name || record.doctorName || "",
      prescriptionNotes: record.prescriptionNotes || "",
      sphRight: record.sphRight || "", cylRight: record.cylRight || "", axisRight: record.axisRight || "", pdRight: record.pdRight || "", vaRight: record.vaRight || "", addRight: record.addRight || "",
      nvSphRight: record.nvSphRight || "", nvCylRight: record.nvCylRight || "", nvAxisRight: record.nvAxisRight || "",
      sphLeft: record.sphLeft || "", cylLeft: record.cylLeft || "", axisLeft: record.axisLeft || "", pdLeft: record.pdLeft || "", vaLeft: record.vaLeft || "", addLeft: record.addLeft || "",
      nvSphLeft: record.nvSphLeft || "", nvCylLeft: record.nvCylLeft || "", nvAxisLeft: record.nvAxisLeft || "",
      clSphRight: record.clSphRight || "", clCylRight: record.clCylRight || "", clAxisRight: record.clAxisRight || "",
      clSphLeft: record.clSphLeft || "", clCylLeft: record.clCylLeft || "", clAxisLeft: record.clAxisLeft || "",
      lensTypes: record.lensTypes || []
    });
    setEditingId(record.id);
    setEditingStoreId(record.parentStoreId);
    setActiveTab("eyewear");
    setIsModalOpen(true);
  };

  const handleFormChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleLensType = (type) => {
    setFormData(prev => {
      const exists = prev.lensTypes.includes(type);
      return { ...prev, lensTypes: exists ? prev.lensTypes.filter(t => t !== type) : [...prev.lensTypes, type] };
    });
  };

  const handleSavePrescription = async () => {
    if (!formData.storeId) return alert("Super Admin: Please assign this record to a specific store.");
    if (!formData.patientName && !formData.patientMobile) return alert("Patient Name or Mobile is required.");
    
    // Attempt to maintain the original timestamp format if editing
    let currentTimestamp = new Date().toISOString();
    if (editingId) {
      const existingRecord = prescriptions.find(p => p.id === editingId);
      if (existingRecord && existingRecord.timestamp) {
        currentTimestamp = existingRecord.timestamp;
      }
    }

    const payload = {
      patient: { name: formData.patientName, mobile: formData.patientMobile, age: formData.patientAge, gender: formData.patientGender },
      doctor: { name: formData.doctorName },
      prescriptionNotes: formData.prescriptionNotes, lensTypes: formData.lensTypes,
      sphRight: formData.sphRight, cylRight: formData.cylRight, axisRight: formData.axisRight, pdRight: formData.pdRight, vaRight: formData.vaRight, addRight: formData.addRight,
      nvSphRight: formData.nvSphRight, nvCylRight: formData.nvCylRight, nvAxisRight: formData.nvAxisRight,
      sphLeft: formData.sphLeft, cylLeft: formData.cylLeft, axisLeft: formData.axisLeft, pdLeft: formData.pdLeft, vaLeft: formData.vaLeft, addLeft: formData.addLeft,
      nvSphLeft: formData.nvSphLeft, nvCylLeft: formData.nvCylLeft, nvAxisLeft: formData.nvAxisLeft,
      clSphRight: formData.clSphRight, clCylRight: formData.clCylRight, clAxisRight: formData.clAxisRight,
      clSphLeft: formData.clSphLeft, clCylLeft: formData.clCylLeft, clAxisLeft: formData.clAxisLeft,
      timestamp: currentTimestamp
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "stores", editingStoreId, "prescriptions", editingId), payload);
      } else {
        await addDoc(collection(db, "stores", formData.storeId, "prescriptions"), payload);
      }
      setIsModalOpen(false);
    } catch (e) { alert("Failed to save prescription: " + e.message); }
  };


  // --- PRINT INDIVIDUAL PDF ---
  const handlePrintPrescription = (record) => {
    const store = stores[record.parentStoreId] || {};
    const includeSignatures = window.confirm("Include Signatures in the printout?");
    const printWindow = window.open('', '_blank');
    const dateObj = parseDate(record.timestamp) || new Date();
    const dateStr = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    
    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; }
      @page { size: A4; margin: 15mm; }
      body { background: #fff; color: #222; -webkit-print-color-adjust: exact; padding: 20px;}
      .header { text-align: center; border-bottom: 2px solid #16205f; padding-bottom: 15px; margin-bottom: 20px; }
      .clinic-name { font-size: 26px; font-weight: 900; color: #16205f; text-transform: uppercase; }
      .store-address { font-size: 12px; color: #555; margin: 8px 0; }
      .title { display: inline-block; font-size: 18px; font-weight: 700; color: #fff; background: #16205f; padding: 6px 20px; border-radius: 20px; }
      .info-box { border: 1px solid #ddd; padding: 15px; background: #fafafa; margin-bottom: 20px; border-radius: 8px;}
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      th, td { border: 1px solid #ddd; padding: 10px; text-align: center; font-size: 13px; }
      th { background: #f0f4f8; font-size: 11px; text-transform: uppercase; }
    </style>
    </head>
    <body>
      <div class="header">
        <div class="clinic-name">${store.storeId || "Shruti Eye Care"}</div>
        <div class="store-address">${store.address || "Main Branch"}<br/>Tel: ${store.phone || "-"}</div>
        <div class="title">Eye Test Prescription</div>
      </div>
      <div class="info-box">
        <p><strong>Patient:</strong> ${record.patient?.name || record.name} | <strong>Mobile:</strong> ${record.patient?.mobile || record.phone} | <strong>Age/Gender:</strong> ${record.patient?.age}/${record.patient?.gender}</p>
        <p><strong>Date:</strong> ${dateStr} | <strong>Optometrist:</strong> ${record.doctor?.name || record.doctorName}</p>
      </div>
      <table>
        <tr><th rowspan="2">EYE</th><th colspan="4">DISTANCE VISION</th><th colspan="3">NEAR VISION</th><th rowspan="2">ADD</th></tr>
        <tr><th>SPH</th><th>CYL</th><th>AXIS</th><th>VA</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr>
        <tr><td>RIGHT (OD)</td><td>${record.sphRight}</td><td>${record.cylRight}</td><td>${record.axisRight}</td><td>${record.vaRight}</td><td>${record.nvSphRight}</td><td>${record.nvCylRight}</td><td>${record.nvAxisRight}</td><td>${record.addRight}</td></tr>
        <tr><td>LEFT (OS)</td><td>${record.sphLeft}</td><td>${record.cylLeft}</td><td>${record.axisLeft}</td><td>${record.vaLeft}</td><td>${record.nvSphLeft}</td><td>${record.nvCylLeft}</td><td>${record.nvAxisLeft}</td><td>${record.addLeft}</td></tr>
      </table>
      ${record.prescriptionNotes ? `<div class="info-box"><strong>Remarks:</strong> ${record.prescriptionNotes}</div>` : ''}
      <script>window.onload = function() { window.print(); setTimeout(() => window.close(), 500); }</script>
    </body>
    </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // --- PRINT GLOBAL DASHBOARD PDF ---
  const handleExportDashboardPDF = (action = 'export') => {
    const printWindow = window.open('', '_blank');
    const dateStr = new Date().toLocaleString();
    
    let tableRows = filteredData.map((record, index) => {
      const storeName = stores[record.parentStoreId]?.storeId || "Unknown";
      return `
        <tr>
          <td>${index + 1}</td>
          <td>${storeName}</td>
          <td>${record.patient?.name || record.name || '-'}</td>
          <td>${record.patient?.mobile || record.phone || '-'}</td>
          <td>${record.doctor?.name || record.doctorName || '-'}</td>
          <td>${formatDate(record.timestamp)}</td>
        </tr>
      `;
    }).join('');

    const autoPrintScript = action === 'export' 
      ? `<script>window.onload = function() { window.print(); setTimeout(() => window.close(), 500); }</script>`
      : '';

    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <style>
      * { font-family: Arial, sans-serif; }
      @page { size: A4 landscape; margin: 15mm; }
      body { padding: 20px; -webkit-print-color-adjust: exact; }
      h1 { color: #16205f; text-align: center; }
      .meta { text-align: center; color: #555; margin-bottom: 30px; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
      th { background: #16205f; color: white; text-transform: uppercase; }
      tr:nth-child(even) { background-color: #f2f2f2; }
    </style>
    </head>
    <body>
      <h1>Super Admin Prescription Report</h1>
      <div class="meta">Generated on: ${dateStr} | Total Records: ${filteredData.length}</div>
      <table>
        <thead>
          <tr>
            <th>#</th><th>Store Branch</th><th>Patient Name</th><th>Mobile</th><th>Optometrist</th><th>Date/Time</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      ${autoPrintScript}
    </body>
    </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const paginatedData = filteredData.slice(0, filters.recordsPerPage);

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans text-base">
      <Sidebar />

      {/* MODAL OVERLAY: ADD / EDIT PRESCRIPTION */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <Stethoscope className="text-blue-600" /> {editingId ? "Edit Prescription (Admin)" : "Add New Prescription (Admin)"}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition text-slate-500 hover:text-rose-500"><X size={20} /></button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* STORE SELECTOR (Super Admin Only) */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                <label className="text-xs uppercase font-black text-blue-800 block mb-2">Target Store Branch *</label>
                <select 
                  value={formData.storeId} 
                  onChange={e => handleFormChange('storeId', e.target.value)} 
                  disabled={!!editingId} // Prevent changing store during edit for safety
                  className="w-full p-2.5 bg-white border border-blue-200 rounded-lg text-sm font-bold outline-none focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">-- Select Store Branch --</option>
                  {Object.values(stores).map(store => (
                    <option key={store.id} value={store.id}>{store.storeId || store.id} - {store.address}</option>
                  ))}
                </select>
              </div>

              {/* Patient & Clinic Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-5 rounded-xl border border-slate-200">
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase text-slate-500 flex items-center gap-2"><UserIcon size={14}/> Patient Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Name</label><input type="text" value={formData.patientName} onChange={e => handleFormChange('patientName', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Mobile</label><input type="tel" value={formData.patientMobile} onChange={e => handleFormChange('patientMobile', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500 font-mono" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Age</label><input type="text" value={formData.patientAge} onChange={e => handleFormChange('patientAge', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Gender</label><select value={formData.patientGender} onChange={e => handleFormChange('patientGender', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500"><option>Male</option><option>Female</option><option>Other</option></select></div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xs font-black uppercase text-slate-500 flex items-center gap-2"><Activity size={14}/> Clinic Info</h3>
                  <div className="space-y-3">
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Optometrist Name</label><input type="text" value={formData.doctorName} onChange={e => handleFormChange('doctorName', e.target.value)} className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-500" /></div>
                    <div><label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Lens Requirements</label>
                      <div className="flex flex-wrap gap-2">
                        {ALL_LENS_TYPES.map(type => (
                          <label key={type} className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                            <input type="checkbox" checked={formData.lensTypes.includes(type)} onChange={() => toggleLensType(type)} className="accent-blue-600"/> {type}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <button onClick={() => setActiveTab("eyewear")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition ${activeTab === "eyewear" ? "bg-slate-100 text-blue-600 border border-b-0 border-slate-200" : "text-slate-500"}`}>Eyewear Power</button>
                <button onClick={() => setActiveTab("contact")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition ${activeTab === "contact" ? "bg-slate-100 text-blue-600 border border-b-0 border-slate-200" : "text-slate-500"}`}>Contact Lens</button>
              </div>

             <div class="section">
          <div class="section-title">Optical Measurement Matrix</div>
          <table>
            <tr>
              <th rowspan="2" style="background:#eef2f6;">EYE</th>
              <th colspan="4">DISTANCE VISION (DV)</th>
              <th colspan="3">NEAR VISION (NV)</th>
              <th rowspan="2" style="background:#eef2f6;">ADD</th>
            </tr>
            <tr>
              <th>SPH</th><th>CYL</th><th>AXIS</th><th>VA</th>
              <th>SPH</th><th>CYL</th><th>AXIS</th>
            </tr>
            <tr>
              <td style="background:#eef2f6;">RIGHT (OD)</td>
              <td>${formData.sphRight || '-'}</td><td>${formData.cylRight || '-'}</td><td>${formData.axisRight || '-'}</td><td>${formData.vaRight || '-'}</td>
              <td>${formData.nvSphRight || '-'}</td><td>${formData.nvCylRight || '-'}</td><td>${formData.nvAxisRight || '-'}</td>
              <td style="color:#b40000;">${formData.addRight || '-'}</td>
            </tr>
            <tr>
              <td style="background:#eef2f6;">LEFT (OS)</td>
              <td>${formData.sphLeft || '-'}</td><td>${formData.cylLeft || '-'}</td><td>${formData.axisLeft || '-'}</td><td>${formData.vaLeft || '-'}</td>
              <td>${formData.nvSphLeft || '-'}</td><td>${formData.nvCylLeft || '-'}</td><td>${formData.nvAxisLeft || '-'}</td>
              <td style="color:#b40000;">${formData.addLeft || '-'}</td>
            </tr>
          </table>
        </div>

        ${(formData.clSphRight || formData.clSphLeft) ? `
        <div class="section">
          <div class="section-title">Contact Lens Specification</div>
          <table>
            <tr><th style="background:#eef2f6;">EYE</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr>
            <tr><td style="background:#eef2f6;">RIGHT (OD)</td><td>${formData.clSphRight || '-'}</td><td>${formData.clCylRight || '-'}</td><td>${formData.clAxisRight || '-'}</td></tr>
            <tr><td style="background:#eef2f6;">LEFT (OS)</td><td>${formData.clSphLeft || '-'}</td><td>${formData.clCylLeft || '-'}</td><td>${formData.clAxisLeft || '-'}</td></tr>
          </table>
        </div>` : ''}


              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Clinical Remarks / Notes</label>
                <textarea value={formData.prescriptionNotes} onChange={e => handleFormChange('prescriptionNotes', e.target.value)} rows="3" className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none focus:border-blue-500 text-sm"></textarea>
              </div>
            </div>

            <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 rounded-xl font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition">Cancel</button>
              <button onClick={handleSavePrescription} className="px-8 py-2.5 rounded-xl font-black uppercase tracking-wide text-white bg-blue-600 hover:bg-blue-700 shadow-md transition">Save Data</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 p-6 lg:p-10 space-y-8 overflow-x-hidden">
        
        {/* HEADER */}
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-3">
              <Activity className="text-blue-600" size={32} /> Super Admin Dashboard
            </h1>
            <p className="text-slate-500 mt-1.5 text-sm font-medium">Global view of all store clinical prescriptions.</p>
          </div>
          <div className="flex items-center gap-3">
            
            {/* CSS-ONLY PDF DROPDOWN */}
            <div className="relative group z-20">
              <button className="px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-colors shadow-sm flex items-center gap-2 cursor-default">
                <Download size={18} /> Report Options <ChevronDown size={16} />
              </button>
              <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden flex flex-col">
                <button onClick={() => handleExportDashboardPDF('preview')} className="text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 border-b border-slate-100 transition-colors">Preview Report</button>
                <button onClick={() => handleExportDashboardPDF('export')} className="text-left px-4 py-3 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">Export / Print</button>
              </div>
            </div>

            <button onClick={handleOpenAddModal} className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors shadow-sm flex items-center gap-2">
              <Plus size={18} /> Add Record
            </button>
          </div>
        </div>

        {/* DASHBOARD WIDGETS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pie Chart Widget */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm lg:col-span-1">
            <h3 className="text-sm font-black uppercase text-slate-500 flex items-center gap-2 mb-4">
              <PieChartIcon size={16} className="text-blue-500"/> Prescriptions By Store
            </h3>
            <div className="h-64 w-full">
              {dashboardStats.pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dashboardStats.pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                      {dashboardStats.pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data available</div>
              )}
            </div>
          </div>

          {/* Summary Table Widget */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm lg:col-span-2 overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-black uppercase text-slate-500 flex items-center gap-2">
                <Activity size={16} className="text-blue-500"/> Global Store Summary
              </h3>
            </div>
            <div className="p-0 overflow-y-auto max-h-64 flex-1">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-white sticky top-0 border-b border-slate-200">
                  <tr>
                    <th className="p-4 font-bold text-slate-600">Store Name</th>
                    <th className="p-4 font-bold text-slate-600">Total Rx</th>
                    <th className="p-4 font-bold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.entries(dashboardStats.storeSummary).map(([storeName, count], idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="p-4 font-bold text-slate-800">{storeName}</td>
                      <td className="p-4 text-blue-600 font-black">{count}</td>
                      <td className="p-4"><span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">Active</span></td>
                    </tr>
                  ))}
                  {Object.keys(dashboardStats.storeSummary).length === 0 && (
                    <tr><td colSpan="3" className="p-6 text-center text-slate-400">No stores with records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* SEARCH & FILTER CONSOLE */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <Filter size={18} className="text-blue-500" />
            <h2 className="text-sm font-black uppercase tracking-wider text-slate-600">Filter Records Globablly</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-1.5 lg:col-span-2 focus-within:border-blue-500 transition-colors">
              <Search className="text-slate-400" size={16} />
              <input type="text" placeholder="Search Patient or Phone..." value={filters.query} onChange={e => setFilters({...filters, query: e.target.value})} className="bg-transparent border-none outline-none w-full text-xs py-1.5 text-slate-800 font-medium" />
            </div>

            <select value={filters.storeId} onChange={e => setFilters({...filters, storeId: e.target.value})} className="p-2 bg-slate-50 border border-slate-200 rounded-xl outline-none text-slate-800 text-xs font-bold">
              <option value="">All Stores</option>
              {Object.values(stores).map(s => <option key={s.id} value={s.id}>{s.storeId}</option>)}
            </select>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <Calendar size={14} className="text-slate-400"/>
              <input type="date" value={filters.startDate} onChange={e => setFilters({...filters, startDate: e.target.value})} className="bg-transparent outline-none font-mono w-full" />
            </div>

            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-600">
              <Calendar size={14} className="text-slate-400"/>
              <input type="date" value={filters.endDate} onChange={e => setFilters({...filters, endDate: e.target.value})} className="bg-transparent outline-none font-mono w-full" />
            </div>

            <select value={filters.doctorName} onChange={e => setFilters({...filters, doctorName: e.target.value})} className="p-2 bg-slate-50 border border-slate-200 rounded-xl outline-none text-slate-800 text-xs font-bold">
              <option value="">All Optometrists</option>
              {uniqueDoctors.map(doc => <option key={doc} value={doc}>{doc}</option>)}
            </select>
          </div>
        </div>

        {/* PRESCRIPTION DATA MATRIX - Compact Card Layout */}
        <div className="space-y-5">
          {paginatedData.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-16 text-center text-slate-500 text-lg font-medium shadow-sm">
              No prescription records found matching your filters.
            </div>
          ) : (
            paginatedData.map((record, index) => {
              const storeName = stores[record.parentStoreId]?.storeId || "Unknown Store";
              return (
                <div key={record.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col xl:flex-row hover:shadow-md transition-shadow relative">
                  
                  {/* Store Badge for Super Admin context */}
                  <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] font-black px-3 py-1 rounded-bl-lg uppercase tracking-wider shadow-sm z-10">
                    {storeName}
                  </div>

                  {/* CUSTOMER DETAILS COLUMN */}
                  <div className="xl:w-1/4 bg-slate-50/50 p-5 border-b xl:border-b-0 xl:border-r border-slate-200 relative pt-8 xl:pt-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-black text-lg border border-blue-100">{index + 1}</div>
                      <div>
                        <h3 className="text-slate-800 font-black text-lg leading-none">{record.patient?.name || record.name || "Walk-in Patient"}</h3>
                        <span className="text-[11px] text-slate-400 font-mono font-bold uppercase tracking-wider">ID: {record.id.slice(-6)}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs text-slate-600 font-medium">
                      <p><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Mobile</strong> <span className="font-mono text-slate-800 font-bold">{record.patient?.mobile || record.phone || "-"}</span></p>
                      <p className="pt-1.5"><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Doctor</strong> <span className="text-slate-800">{record.doctor?.name || record.doctorName || "-"}</span></p>
                      <p className="pt-1.5"><strong className="text-slate-500 uppercase text-[10px] block mb-0.5">Date</strong> <span className="font-mono text-slate-800">{formatDate(record.timestamp)}</span></p>
                    </div>
                  </div>

                  {/* EYEWEAR POWER DETAILS COLUMN */}
                  <div className="xl:w-1/2 p-5 border-b xl:border-b-0 xl:border-r border-slate-200 bg-white">
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Eye size={14} className="text-blue-500"/> Eyewear Power</h4>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-center text-[10px] font-mono border-collapse">
                        <thead>
                          <tr>
                            <th className="border-b border-r border-slate-200 bg-slate-50 p-2 text-slate-500 font-bold" rowSpan={2}>TYPE</th>
                            <th className="border-b border-r border-slate-200 bg-blue-50 text-blue-700 p-2 font-black" colSpan={5}>RIGHT EYE (OD)</th>
                            <th className="border-b border-slate-200 bg-purple-50 text-purple-700 p-2 font-black" colSpan={5}>LEFT EYE (OS)</th>
                          </tr>
                          <tr className="bg-slate-50 text-slate-500 uppercase">
                            <th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">AXIS</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">PD</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">VA</th>
                            <th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">AXIS</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">PD</th><th className="border-b border-slate-200 p-1.5 font-bold">VA</th>
                          </tr>
                        </thead>
                        <tbody className="text-slate-800">
                          <tr className="hover:bg-slate-50/50">
                            <td className="border-b border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">DV</td>
                            <td className="border-b border-r border-slate-200 p-2 font-bold">{record.sphRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.cylRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.axisRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.pdRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.vaRight || "-"}</td>
                            <td className="border-b border-r border-slate-200 p-2 font-bold">{record.sphLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.cylLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.axisLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.pdLeft || "-"}</td><td className="border-b border-slate-200 p-2 font-bold">{record.vaLeft || "-"}</td>
                          </tr>
                          <tr className="hover:bg-slate-50/50">
                            <td className="border-b border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">NV</td>
                            <td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvSphRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvCylRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvAxisRight || "-"}</td><td className="border-b border-r border-slate-200 p-2 bg-slate-50 text-slate-300" colSpan={2}>-</td>
                            <td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvSphLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvCylLeft || "-"}</td><td className="border-b border-r border-slate-200 p-2 font-bold">{record.nvAxisLeft || "-"}</td><td className="border-b border-slate-200 p-2 bg-slate-50 text-slate-300" colSpan={2}>-</td>
                          </tr>
                          <tr className="hover:bg-slate-50/50">
                            <td className="border-r border-slate-200 p-2 font-bold bg-slate-50 text-slate-500">ADD</td>
                            <td className="border-r border-slate-200 p-2 text-blue-600 font-black" colSpan={5}>{record.addRight || "-"}</td>
                            <td className="p-2 text-purple-600 font-black" colSpan={5}>{record.addLeft || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* CONTACT LENS & ACTIONS COLUMN */}
                  <div className="xl:w-1/4 p-5 flex flex-col justify-between bg-white">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Users size={14} className="text-blue-500"/> Contact Lens</h4>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-center text-[10px] font-mono border-collapse">
                          <thead>
                            <tr className="bg-slate-50 text-slate-500">
                              <th className="border-b border-r border-slate-200 p-1.5 font-bold">EYE</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">SPH</th><th className="border-b border-r border-slate-200 p-1.5 font-bold">CYL</th><th className="border-b border-slate-200 p-1.5 font-bold">AXIS</th>
                            </tr>
                          </thead>
                          <tbody className="text-slate-800">
                            <tr className="hover:bg-slate-50/50"><td className="border-b border-r border-slate-200 p-1.5 font-black bg-blue-50 text-blue-700">OD</td><td className="border-b border-r border-slate-200 p-1.5 font-bold">{record.clSphRight || "-"}</td><td className="border-b border-r border-slate-200 p-1.5 font-bold">{record.clCylRight || "-"}</td><td className="border-b border-slate-200 p-1.5 font-bold">{record.clAxisRight || "-"}</td></tr>
                            <tr className="hover:bg-slate-50/50"><td className="border-r border-slate-200 p-1.5 font-black bg-purple-50 text-purple-700">OS</td><td className="border-r border-slate-200 p-1.5 font-bold">{record.clSphLeft || "-"}</td><td className="border-r border-slate-200 p-1.5 font-bold">{record.clCylLeft || "-"}</td><td className="p-1.5 font-bold">{record.clAxisLeft || "-"}</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                       <button onClick={() => handleOpenEditModal(record)} className="p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-600 transition-colors" title="Edit Prescription"><Edit3 size={16} /></button>
                       <button onClick={() => handleDelete(record.parentStoreId, record.id)} className="p-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-xl text-rose-600 transition-colors" title="Delete Prescription"><Trash2 size={16} /></button>
                       <button onClick={() => handlePrintPrescription(record)} className="flex-1 p-2 bg-blue-600 hover:bg-blue-700 rounded-xl text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm flex items-center justify-center gap-2">
                         <Printer size={14}/> Print
                       </button>
                    </div>
                  </div>

                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
}